import { PropsWithChildren, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { toast } from 'sonner';

import { CurrencySelect, ICurrency } from '@/entities/currency';
import { useVabsAccountsStore } from '@/entities/currency-account';
import { createAccountMutation } from '@/entities/currency-account/api/mutations';

import { Plus } from '@/shared/assets';
import { COLORS, cn } from '@/shared/lib';
import { Button, DashboardDialog, DashboardDialogProps } from '@/shared/ui';

interface AddAccountContentProps {
  onConfirmAdding?: VoidFunction;
}

const Content = ({ onConfirmAdding }: AddAccountContentProps) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState<ICurrency | null>(
    null,
  );

  const vAccountId = useVabsAccountsStore((state) => state.accountId);
  const accounts = useVabsAccountsStore((state) => state.accounts);
  const tickers = accounts.map((i) => i.code);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: createAccountMutation,
    onSuccess: async () => {
      //TODO need to change on setQueryData
      await queryClient.invalidateQueries({
        queryKey: ['vabs-accounts'],
      });
      toast.success(`${selectedCurrency?.code || 'New'} account request sent`);
      onConfirmAdding?.();
      setIsOpen(false);
    },
  });

  const onConfirm = async () => {
    if (!selectedCurrency) return;
    await mutateAsync({
      currencyId: selectedCurrency.id,
      accountId: vAccountId,
    });
  };

  return (
    <div
      className={cn('flex flex-col gap-5 pb-5 pt-4', {
        'pb-0': selectedCurrency,
      })}
    >
      {selectedCurrency && (
        <p className="mb-3 mt-[-20px] w-full text-center text-base-300 typography-16r">
          Active in 3 working days
        </p>
      )}
      <CurrencySelect
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        searchValue={searchValue}
        iconColor={COLORS['icon-base-300']}
        setSearchValue={setSearchValue}
        selectedCurrency={selectedCurrency}
        setSelectedCurrency={setSelectedCurrency}
        excludedTickers={tickers}
      />
      {!selectedCurrency && (
        <p className="text-center text-base-100 typography-14r">
          Open balances in multiple currencies
        </p>
      )}
      {selectedCurrency && (
        <>
          <p className="mb-1 px-3 text-center text-base-100 typography-14r">
            Your {selectedCurrency.code} balance comes with an account number,
            an institution number and a transit&nbsp;number. You&apos;ll be able
            to share these with people who want to send {selectedCurrency.code}{' '}
            to you
          </p>

          <Button
            variant="contrast-500"
            size="lg"
            className="w-full"
            onClick={onConfirm}
            loading={isPending}
          >
            Send request
          </Button>
        </>
      )}
    </div>
  );
};
interface AddAccountProps extends Omit<DashboardDialogProps, 'content'> {
  onConfirmAdding?: VoidFunction;
}
export const AddAccount = ({
  children,
  onConfirmAdding,
  ...props
}: PropsWithChildren<AddAccountProps>) => {
  return (
    <DashboardDialog
      animationProps={{
        isFirst: true,
      }}
      title="Open a balance"
      modalProps={{
        classNames: { title: 'whitespace-wrap max-w-[300px] !text-[40px]' },
      }}
      content={<Content onConfirmAdding={onConfirmAdding} />}
      {...props}
    >
      {children ?? (
        <Button
          size="lg"
          variant="base-300"
          className="w-full justify-start gap-4 rounded-none"
        >
          <Plus className="!size-6" />
          Add new account
        </Button>
      )}
    </DashboardDialog>
  );
};
